import React, {useEffect, useState} from "react";
import {UserApiCalls} from "../../apis/UserApiCalls";
import {
    BUTTON,
    CSM_USER_TABLE_GRANTED_ACCESS_HEADER,
    CSM_USER_TABLE_USER_NAME_HEADER, DEFAULT_ROWS_PER_PAGE,
    EMAIL_TEXT, FIRST_NAME, LAST_NAME,
    PPT_POLLS_TABLE_ACTION_HEADER,
    ROLE, ROLES, SEARCH_TEXT, YUJA_INST_CODE,
} from "../../utils/constants";
import classes from "./InstitutionManagementPage.module.css";
import Skeleton, {SkeletonTheme} from "react-loading-skeleton";
import {YuJaTextBox} from "../../components/standardization/YuJaTextBox";
import YuJaButton from "../../components/standardization/YuJaButton";
import { ReactComponent as SearchIcon } from "../../images/search_bar_icon.svg";
import {ReactComponent as CLOSE} from "../../images/Close.svg";
import {ReactComponent as NoResultImage} from "../../images/no-result-found.svg";
import {ReactComponent as PLUS} from "../../images/plus.svg";
import {InstitutionApiCalls} from "../../apis/InstitutionApiCalls";
import {useCSMAccessCheck} from "../../hooks/useCSMAccessCheck";
import CSMManagementTable from "./CSMManagementTable";
import CSMModal from "./CSMModal";
import CSMAccessModal from "./CSMAccessModal";
import {useConfig} from "../../hooks/useConfig";
import {Button} from "@base-ui/react/button";

export default function CSMManagementPage({setPageIdentifier}) {
    const {loadUsers} = UserApiCalls();
    const {loadInstitutions} = InstitutionApiCalls();
    const {checkIsCSMAdmin} = useCSMAccessCheck();
    let config = useConfig();

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [keyword, setKeyword] = useState("");
    const [filter, setFilter] = useState({});
    const [CSMModalShown, setCSMModalShown] = useState(false);
    const [CSMAccessModalShown, setCSMAccessModalShown] = useState(false);
    const [editUser, setEditUser] = useState({});
    const [rawInstArr, setRawInstArr] = useState([]);
    const [grantedInstArr, setGrantedInstArr] = useState([]);
    const [notGrantedInstArr, setNotGrantedInstArr] = useState([]);

    useEffect(() => {
        setPageIdentifier("CSM Management");
    }, []);

    useEffect(() => {
        loadData();
    }, []);

    const loadData =  () => {
        setLoading(true);
        Promise.all([loadUsers(), loadInstitutions()]).then(([users, institutions]) => {
            const accessCounts = {};
            const now = new Date();
            institutions = institutions.filter(inst => inst.code !== YUJA_INST_CODE).map(inst => {
                inst.domain = !!inst.shortName ? `${inst.shortName}.${config.host_url}` : "-";
                return inst;
            });
            setRawInstArr(JSON.parse(JSON.stringify(institutions)));
            institutions.forEach(inst => {
                try {
                    const managers = !!inst.adminManagers ? JSON.parse(inst.adminManagers) : [];

                    managers.forEach(manager => {
                        const id = manager.id;
                        let hasAccess = false;

                        // 1. Check if the user has "Forever Access" (no dates provided)
                        if (!manager.accessStartDate || !manager.accessEndDate) {
                            hasAccess = true;
                        } else {
                            // 2. Check if current date is within the range
                            const start = new Date(manager.accessStartDate);
                            const end = new Date(manager.accessEndDate);

                            if (now >= start && now <= end) {
                                hasAccess = true;
                            }
                        }

                        // 3. If they have access, increment the count for this userId
                        if (hasAccess) {
                            accessCounts[id] = (accessCounts[id] || 0) + 1;
                        }
                    });
                } catch (e) {
                    console.error("Invalid JSON in institution:", inst.institutionId);
                }
            });



            users = users.map(user => {
                    user.isAdmin = checkIsCSMAdmin(user.role, user.email);
                    user.accessCount = accessCounts[user.userId] || 0;
                    user.realRole = user.role;
                    user.role = user.isAdmin ? "CSM Admin" : "CSM";
                    return user;
                }).sort((userA, userB) => {
                    const {updateTime: updateTimeA=""} = userA;
                    const {updateTime: updateTimeB=""} = userB;
                    if (updateTimeA > updateTimeB) {
                        return -1;
                    } else if (updateTimeA < updateTimeB) {
                        return 1;
                    } else {
                        return 0;
                    }
                });
            setData(!!users ? users : []);
        }).finally(() => {
            setLoading(false);
        });
    }

    let columns =  [
        {Header: EMAIL_TEXT, accessor: "email", width: 200},
        {Header: CSM_USER_TABLE_USER_NAME_HEADER, accessor: "userName", width: 120},
        {Header: FIRST_NAME, accessor: "firstName", width: 120},
        {Header: LAST_NAME, accessor: "lastName", width: 120},
        {Header: ROLE, accessor: "role", width: 100},
        {Header: CSM_USER_TABLE_GRANTED_ACCESS_HEADER, accessor: "accessCount", width: 240},
        {Header: PPT_POLLS_TABLE_ACTION_HEADER, accessor: "action", width: 80}
    ];

    const clickSearch = () => {
        setFilter({ keyword: keyword});
    };

    const onEdit = (user) => {
        let role = Object.entries(ROLES).map(r => r[1].value).includes(user.realRole)
            ? ROLES.ADMINISTRATOR.value
            : user.realRole;
        role = !!role ? role : ROLES.ADMINISTRATOR.value;
        const copy = {
            userName: user.userName,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            userId: user.userId,
            phone: user.phone,
            role: role
        };
        setEditUser(copy);
        setCSMModalShown(true);
    }


    const onInstitutionAccess = (user) => {
        // rawInstArr
        const userId = user.userId;
        let copy = JSON.parse(JSON.stringify(rawInstArr));
        const now = new Date();

        const result = copy.map(inst => {
            delete inst.ssoConfig;
            delete inst.platformSetting;
            return inst;
        }).reduce((acc, inst) => {
            // Parse the string and check for the ID
            const managers = !!inst.adminManagers ? JSON.parse(inst.adminManagers) : [];
            const canAccess = managers.some(manager => {
                if (manager.id !== userId) {
                    return false;
                }

                let hasAccess = false;
                if (!manager.accessStartDate || !manager.accessEndDate) {
                    hasAccess = true;
                } else {
                    // 2. Check if current date is within the range
                    const start = new Date(manager.accessStartDate);
                    const end = new Date(manager.accessEndDate);

                    if (now >= start && now <= end) {
                        hasAccess = true;
                    }
                }

                if (hasAccess && !manager.role) {
                    manager.role = ROLES.IT_MANAGER.value;
                    inst.adminManagers = JSON.stringify(managers);
                }

                return hasAccess;
            });

            // Push to the corresponding array
            acc[canAccess ? 'accessible' : 'inaccessible'].push(inst);

            return acc;
        }, { accessible: [], inaccessible: [] });

        setEditUser(JSON.parse(JSON.stringify(user)));
        setGrantedInstArr(result.accessible);
        setNotGrantedInstArr(result.inaccessible);
        setCSMAccessModalShown(true);
    }


    if (loading) {
        return (
            <div className={classes.parentContainer}>
                <div className={classes.filterCard} style={{marginTop: 20}}>
                    <div
                        style={{
                            flex: 1,
                            height: 35,
                            minWidth: 200,
                            maxWidth: 1000,
                        }}
                    >
                        <Skeleton borderRadius={4} height={"100%"} inline={true} width={"100%"}/>
                    </div>
                    <Skeleton borderRadius={4} height={35} inline={true} width={75}/>

                    <Skeleton borderRadius={4} height={35} inline={true} width={110}/>
                </div>

                <div className={classes.tableContainerCard} style={{overflow: "scroll"}}>
                    <div className={classes.skeletonTableContainer}>
                        <SkeletonTheme color="rgba(237, 237, 237, 1)" highlightColor="lightgrey">
                            <div className={classes.skeletonHeaderContainer}>
                                <Skeleton borderRadius={10} height={20} inline={true} width="10vw"
                                          containerClassName={classes.skeletonBodyCellWrapper}/>
                                <Skeleton borderRadius={10} height={20} inline={true} width="6vw"
                                          containerClassName={classes.skeletonBodyCellWrapper}/>
                                <Skeleton borderRadius={10} height={20} inline={true} width="6vw"
                                          containerClassName={classes.skeletonBodyCellWrapper}/>
                                <Skeleton borderRadius={10} height={20} inline={true} width="5vw"
                                          containerClassName={classes.skeletonBodyCellWrapper}/>
                                <Skeleton borderRadius={10} height={20} inline={true} width="4vw"
                                          containerClassName={classes.skeletonBodyCellWrapper}/>

                            </div>
                            <div>
                                {Array.apply(null, Array(DEFAULT_ROWS_PER_PAGE)).map((_, idx) => {
                                    return (
                                        <div className={classes.skeletonBodyRow} key={idx}>
                                            <Skeleton
                                                borderRadius={10}
                                                height={20}
                                                inline={true}
                                                width={`${5 + (Math.random() * 8)}vw`}
                                                containerClassName={classes.skeletonBodyCellWrapper}
                                            />
                                            <Skeleton
                                                borderRadius={10}
                                                height={20}
                                                inline={true}
                                                width={`${5 + Math.random()}vw`}
                                                containerClassName={classes.skeletonBodyCellWrapper}
                                            />
                                            <Skeleton
                                                borderRadius={10}
                                                height={20}
                                                inline={true}
                                                width={`${8 + (Math.random() * 2)}vw`}
                                                containerClassName={classes.skeletonBodyCellWrapper}
                                            />
                                            <Skeleton
                                                borderRadius={10}
                                                height={20}
                                                inline={true}
                                                width={`${6 + Math.random()}vw`}
                                                containerClassName={classes.skeletonBodyCellWrapper}
                                            />
                                            <Skeleton
                                                borderRadius={10}
                                                height={20}
                                                inline={true}
                                                width={`${3 + (Math.random() * 2)}vw`}
                                                containerClassName={classes.skeletonBodyCellWrapper}
                                            />
                                        </div>
                                    )
                                })}
                            </div>
                        </SkeletonTheme>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={classes.parentContainer}>
            <div className={classes.filterCard} style={{marginTop: 20}}>
                <YuJaTextBox
                    containerStyle={{
                        flex: 1,
                        height: 35,
                        minWidth: 200,
                        maxWidth: 1000,
                        fontSize: 14,
                        lineHeight: 1.6,
                    }}
                    onChange={e => setKeyword(e.target.value)}
                    onKeyUp={e => {
                        if (e.key === 'Enter') {
                            clickSearch();
                        }
                    }}
                    value={keyword}
                    before={<SearchIcon style={{marginRight: 5, width: 16}}/>}
                    after={
                        !!keyword
                            ? (
                                <Button
                                    aria-label={"Clear search keyword"}
                                    className={classes.closeIcon}
                                    onClick={() => {
                                        setKeyword("");
                                        setFilter({keyword: ""});
                                    }}
                                >
                                    <CLOSE style={{width: 16, height: 16}}/>
                                </Button>
                            )
                            : (<></>)
                    }
                    placeholder={"Search by first name, last name, user name or email"}
                />

                <YuJaButton style={{height: 35}} onClick={clickSearch}>
                    {SEARCH_TEXT}
                </YuJaButton>

                {/*<YuJaButton*/}
                {/*    style={{height: 35}}*/}
                {/*    onClick={() => {*/}
                {/*        setEditUser({});*/}
                {/*        setCSMModalShown(true);*/}
                {/*    }}*/}
                {/*>*/}
                {/*    <PLUS style={{width: 18, height: 18, marginRight: 3}}/>*/}
                {/*    {"Create CSM"}*/}
                {/*</YuJaButton>*/}
            </div>

            {!data.length &&
                <div className={classes.tableContainerCard}>
                    <NoResultImage style={{height: 150, alignSelf: "center", margin: "20px 0"}}/>
                    <span style={{alignSelf: "center", fontSize: 20, fontWeight: "600", lineHeight: 1.4}}>
                        No CSM found
                    </span>
                    <span style={{alignSelf: "center"}}>
                        Try broadening your search criteria to find what you are looking for or creating a new CSM.
                    </span>
                </div>
            }

            {!!data.length &&
                <CSMManagementTable
                    data={data}
                    setData={setData}
                    columns={columns}
                    globalFilterObj={filter}
                    onEdit={onEdit}
                    onInstitutionAccess={onInstitutionAccess}
                />
            }

            <CSMModal
                show={CSMModalShown}
                setModalShow={setCSMModalShown}
                reload={loadData}
                user={editUser}
            />
            <CSMAccessModal
                show={CSMAccessModalShown}
                setModalShow={setCSMAccessModalShown}
                user={editUser}
                grantedInstArr={grantedInstArr}
                notGrantedInstArr={notGrantedInstArr}
                reload={loadData}
            />
        </div>
    );

}